import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { ReactNode } from 'react';

export type FieldEditModalHeaderAction = {
  ariaLabel: string;
  onAction: () => void;
};

type FieldEditModalHeaderActionContextValue = {
  /** True only inside a FieldEditModal, so fields can tell a modal from an in-page section. */
  isInFieldEditModal: boolean;
  /** True whenever a provider is present, whether that is the modal or an in-page section. */
  hasHeaderActionHost: boolean;
  headerAction?: FieldEditModalHeaderAction;
  registerHeaderAction: (action?: FieldEditModalHeaderAction) => void;
};

const FieldEditModalHeaderActionContext = createContext<FieldEditModalHeaderActionContextValue>({
  isInFieldEditModal: false,
  hasHeaderActionHost: false,
  registerHeaderAction: () => {},
});

export const FieldEditModalHeaderActionProvider = ({
  children,
  isInFieldEditModal = true,
}: {
  children: ReactNode;
  isInFieldEditModal?: boolean;
}) => {
  const [headerAction, setHeaderAction] = useState<FieldEditModalHeaderAction | undefined>();

  const registerHeaderAction = useCallback((action?: FieldEditModalHeaderAction) => setHeaderAction(action), []);

  const contextValue = useMemo(
    () => ({ isInFieldEditModal, hasHeaderActionHost: true, headerAction, registerHeaderAction }),
    [isInFieldEditModal, headerAction, registerHeaderAction],
  );

  return (
    <FieldEditModalHeaderActionContext.Provider value={contextValue}>
      {children}
    </FieldEditModalHeaderActionContext.Provider>
  );
};

export const useFieldEditModalHeaderAction = () => useContext(FieldEditModalHeaderActionContext);

export const useRegisterFieldEditModalHeaderAction = (action?: FieldEditModalHeaderAction) => {
  const { hasHeaderActionHost, registerHeaderAction } = useFieldEditModalHeaderAction();

  // NOTE: Keep `action` out of the effect deps. It is a new object each render, so depending on it loops.
  const actionRef = useRef(action);
  actionRef.current = action;

  const ariaLabel = action?.ariaLabel;

  useEffect(() => {
    if (!hasHeaderActionHost || !ariaLabel) {
      return;
    }

    registerHeaderAction({ ariaLabel, onAction: () => actionRef.current?.onAction() });

    return () => registerHeaderAction(undefined);
  }, [hasHeaderActionHost, registerHeaderAction, ariaLabel]);

  return hasHeaderActionHost;
};
